import React, { useState, useEffect } from 'react';
import { 
  Plus, Trash2, Loader2 
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from '../../ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CreateWeekenderDialog - Extracted from AdminWeekendsTab.js (Phase 3G)
 * Handles both Create and Edit operations for weekend events.
 * 
 * Props:
 * - open: boolean - controls dialog visibility
 * - onClose: function - called when dialog should close
 * - event: object|null - null for Create mode, event object for Edit mode
 * - authToken: string - auth token for API calls
 * - onSuccess: function - called after successful create/update to refresh event list
 */
export default function CreateWeekenderDialog({
  open,
  onClose,
  event,
  authToken,
  onSuccess
}) {
  const [saving, setSaving] = useState(false);
  
  // Form state - mirrors exactly the legacy implementation from AdminWeekendsTab.js
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    venue_name: '',
    venue_address: '',
    start_date: '',
    end_date: '',
    image_url: '',
    room_deposit_amount: 50,
    max_capacity: '',
    is_published: false,
    booking_deadline: '',
    payment_plan_start_date: '',
    payment_plan_end_date: '',
    additional_info: '',
    event_type: 'dance_holiday',
    room_options: [{ type: 'single', price_per_person: 0, description: '', category: 'room', pricing_type: 'per_person' }],
    ticket_options: [],
    pitch_deposit_amount: 25,
    add_ons: [],
    no_deposit_option: false
  });

  // Initialize form when dialog opens
  useEffect(() => {
    if (!open) return;
    
    if (event) {
      // Edit mode: load event data
      setFormData({
        title: event.title || '',
        slug: event.slug || '',
        description: event.description || '',
        venue_name: event.venue_name || '',
        venue_address: event.venue_address || '',
        start_date: event.start_date ? event.start_date.split('T')[0] : '',
        end_date: event.end_date ? event.end_date.split('T')[0] : '',
        image_url: event.image_url || '',
        room_deposit_amount: event.room_deposit_amount || 50,
        max_capacity: event.max_capacity || '',
        is_published: event.is_published || false,
        booking_deadline: event.booking_deadline ? event.booking_deadline.split('T')[0] : '',
        payment_plan_start_date: event.payment_plan_start_date ? event.payment_plan_start_date.split('T')[0] : '',
        payment_plan_end_date: event.payment_plan_end_date ? event.payment_plan_end_date.split('T')[0] : '',
        additional_info: event.additional_info || '',
        event_type: event.event_type || 'dance_holiday',
        room_options: event.room_options?.length > 0 
          ? event.room_options 
          : [{ type: 'single', price_per_person: 0, description: '', category: 'room', pricing_type: 'per_person' }],
        ticket_options: event.ticket_options || [],
        pitch_deposit_amount: event.pitch_deposit_amount || 25,
        add_ons: event.add_ons || [],
        no_deposit_option: event.no_deposit_option || false
      });
    } else {
      // Create mode: reset to defaults
      resetForm();
    }
  }, [open, event]);

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      description: '',
      venue_name: '',
      venue_address: '',
      start_date: '',
      end_date: '',
      image_url: '',
      room_deposit_amount: 50,
      max_capacity: '',
      is_published: false,
      booking_deadline: '',
      payment_plan_start_date: '',
      payment_plan_end_date: '',
      additional_info: '',
      event_type: 'dance_holiday',
      room_options: [{ type: 'single', price_per_person: 0, description: '', category: 'room', pricing_type: 'per_person' }],
      ticket_options: [],
      pitch_deposit_amount: 25,
      add_ons: [],
      no_deposit_option: false
    });
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.venue_name || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate Final Balance Due Date is required and before event start
    if (!formData.payment_plan_end_date) {
      toast.error('Final Balance Due Date is required');
      return;
    }
    
    if (new Date(formData.payment_plan_end_date) >= new Date(formData.start_date)) {
      toast.error('Final Balance Due Date must be before the event start date');
      return;
    }

    setSaving(true);
    try {
      const url = event 
        ? `${API_URL}/api/weekends/admin/events/${event.event_id}`
        : `${API_URL}/api/weekends/admin/events`;
      
      const response = await fetch(url, {
        method: event ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          ...formData,
          start_date: new Date(formData.start_date).toISOString(),
          end_date: new Date(formData.end_date).toISOString(),
          booking_deadline: formData.booking_deadline ? new Date(formData.booking_deadline).toISOString() : null,
          payment_plan_start_date: formData.payment_plan_start_date ? new Date(formData.payment_plan_start_date).toISOString() : null,
          payment_plan_end_date: formData.payment_plan_end_date ? new Date(formData.payment_plan_end_date).toISOString() : null,
          max_capacity: formData.max_capacity ? parseInt(formData.max_capacity) : null,
          room_deposit_amount: parseFloat(formData.room_deposit_amount) || 50
        })
      });

      if (response.ok) {
        toast.success(event ? 'Event updated!' : 'Event created!');
        onClose();
        onSuccess?.();
        if (!event) {
          resetForm();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save event');
      }
    } catch (error) {
      toast.error('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  // ============== ROOM OPTION HELPERS ==============
  
  const addRoomOption = () => {
    setFormData(prev => ({
      ...prev,
      room_options: [...prev.room_options, { 
        type: '', 
        price_per_person: 0, 
        price_per_unit: 0,
        description: '', 
        available_count: null, 
        booked_count: 0,
        category: 'room',
        pricing_type: 'per_person',
        max_occupancy: null,
        includes_ticket: false,
        amenities: []
      }]
    }));
  };

  const updateRoomOption = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.room_options];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-set pricing_type based on category
      if (field === 'category') {
        if (value === 'camping') {
          updated[index].pricing_type = 'per_unit';
        } else if (value === 'room') {
          updated[index].pricing_type = 'per_person';
        }
      }
      return { ...prev, room_options: updated };
    });
  };

  const removeRoomOption = (index) => {
    setFormData(prev => ({
      ...prev,
      room_options: prev.room_options.filter((_, i) => i !== index)
    }));
  };

  // ============== TICKET OPTION HELPERS ==============
  
  const addTicketOption = () => {
    setFormData(prev => ({
      ...prev,
      ticket_options: [...(prev.ticket_options || []), { 
        type: '', 
        price: 0, 
        description: '', 
        available_count: null, 
        booked_count: 0,
        valid_dates: []
      }]
    }));
  };

  const updateTicketOption = (index, field, value) => {
    setFormData(prev => {
      const updated = [...(prev.ticket_options || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, ticket_options: updated };
    });
  };

  const removeTicketOption = (index) => {
    setFormData(prev => ({
      ...prev,
      ticket_options: (prev.ticket_options || []).filter((_, i) => i !== index)
    }));
  };

  // ============== ADD-ON HELPERS ==============
  
  const addAddOn = () => {
    setFormData(prev => ({
      ...prev,
      add_ons: [...prev.add_ons, { name: '', price: 0, description: '', deposit_amount: 0 }]
    }));
  };

  const updateAddOn = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.add_ons];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, add_ons: updated };
    });
  };

  const removeAddOn = (index) => {
    setFormData(prev => ({
      ...prev,
      add_ons: prev.add_ons.filter((_, i) => i !== index)
    }));
  };

  // Handle dialog close - reset form if in create mode
  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-slate-900">
        <DialogHeader>
          <DialogTitle className="text-slate-900">{event ? 'Edit' : 'Create'} Weekend Event</DialogTitle>
          <DialogDescription className="text-slate-600">
            Fill in the details for your line dance weekend event
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-slate-700">Event Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g. Spring Dance Weekend 2026"
                className="bg-white border-slate-300"
                data-testid="event-title-input"
              />
            </div>
            <div>
              <Label className="text-slate-700">URL Slug</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') }))}
                placeholder="auto-generated if empty"
                className="bg-white border-slate-300"
                data-testid="event-slug-input"
              />
              {formData.slug && (
                <p className="text-xs text-slate-500 mt-1">
                  URL: linedancecentral.uk/weekends/<span className="font-medium text-violet-600">{formData.slug}</span>
                </p>
              )}
            </div>
            <div>
              <Label className="text-slate-700">Image URL</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                placeholder="https://..."
                className="bg-white border-slate-300"
                data-testid="event-image-input"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-slate-700">Description *</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe your event..."
              rows={4}
              className="bg-white border-slate-300"
              data-testid="event-description-input"
            />
          </div>

          {/* Venue */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-700">Venue Name *</Label>
              <Input
                value={formData.venue_name}
                onChange={(e) => setFormData(prev => ({ ...prev, venue_name: e.target.value }))}
                placeholder="e.g. The Grand Hotel"
                className="bg-white border-slate-300"
                data-testid="event-venue-name-input"
              />
            </div>
            <div>
              <Label className="text-slate-700">Venue Address *</Label>
              <Input
                value={formData.venue_address}
                onChange={(e) => setFormData(prev => ({ ...prev, venue_address: e.target.value }))}
                placeholder="Full address"
                className="bg-white border-slate-300"
                data-testid="event-venue-address-input"
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-700">Start Date *</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                className="bg-white border-slate-300"
                data-testid="event-start-date-input"
              />
            </div>
            <div>
              <Label className="text-slate-700">End Date *</Label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                className="bg-white border-slate-300"
                data-testid="event-end-date-input"
              />
            </div>
            <div>
              <Label className="text-slate-700">Booking Deadline</Label>
              <Input
                type="date"
                value={formData.booking_deadline}
                onChange={(e) => setFormData(prev => ({ ...prev, booking_deadline: e.target.value }))}
                className="bg-white border-slate-300"
                data-testid="event-booking-deadline-input"
              />
            </div>
            <div>
              <Label className="text-slate-700">Payment Plan Start Date</Label>
              <Input
                type="date"
                value={formData.payment_plan_start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_plan_start_date: e.target.value }))}
                className="bg-white border-slate-300"
                data-testid="event-payment-start-input"
              />
              <p className="text-xs text-slate-500 mt-1">First payment date (1st of month, 10am)</p>
            </div>
            <div>
              <Label className="text-slate-700">Final Balance Due Date <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={formData.payment_plan_end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_plan_end_date: e.target.value }))}
                className="bg-white border-slate-300"
                required
                max={formData.start_date || undefined}
                data-testid="event-payment-end-input"
              />
              <p className="text-xs text-slate-500 mt-1">Deadline for all payments (must be before event)</p>
            </div>
          </div>

          {/* Settings */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label className="text-slate-700">Event Type</Label>
              <select
                value={formData.event_type || 'dance_holiday'}
                onChange={(e) => setFormData(prev => ({ ...prev, event_type: e.target.value }))}
                className="w-full h-10 px-3 rounded-md bg-white border border-slate-300 text-slate-900"
                data-testid="event-type-select"
              >
                <option value="dance_holiday">Dance Holiday</option>
                <option value="festival">Festival</option>
                <option value="day_event">Day Event</option>
              </select>
            </div>
            <div>
              <Label className="text-slate-700">Room Deposit (£/person)</Label>
              <Input
                type="number"
                value={formData.room_deposit_amount}
                onChange={(e) => setFormData(prev => ({ ...prev, room_deposit_amount: e.target.value }))}
                min="0"
                step="0.01"
                placeholder="e.g. 50"
                className="bg-white border-slate-300"
                data-testid="event-room-deposit-input"
              />
            </div>
            <div>
              <Label className="text-slate-700">Pitch Deposit (£/pitch)</Label>
              <Input
                type="number"
                value={formData.pitch_deposit_amount || 25}
                onChange={(e) => setFormData(prev => ({ ...prev, pitch_deposit_amount: e.target.value }))}
                min="0"
                step="0.01"
                placeholder="e.g. 25"
                className="bg-white border-slate-300"
                data-testid="event-pitch-deposit-input"
              />
            </div>
            <div>
              <Label className="text-slate-700">Max Capacity</Label>
              <Input
                type="number"
                value={formData.max_capacity}
                onChange={(e) => setFormData(prev => ({ ...prev, max_capacity: e.target.value }))}
                placeholder="Unlimited"
                className="bg-white border-slate-300"
                data-testid="event-max-capacity-input"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
                data-testid="event-published-switch"
              />
              <Label className="text-slate-700">Published</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.no_deposit_option}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, no_deposit_option: checked }))}
                data-testid="no-deposit-toggle"
              />
              <Label className="text-slate-700">
                <span className="flex items-center gap-1">
                  No Deposit Option
                  <span className="text-xs text-emerald-600">(Promotion)</span>
                </span>
              </Label>
            </div>
          </div>
          
          {formData.no_deposit_option && (
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-sm text-emerald-800">
                <strong>No Deposit Promotion Enabled:</strong> Logged-in users can book without paying a deposit. 
                They'll receive a booking confirmation and can pay the full amount later.
              </p>
            </div>
          )}

          {/* Accommodation Options */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-slate-700">
                {formData.event_type === 'festival' ? 'Accommodation / Pitches' : 'Room Options'}
              </Label>
              <Button type="button" variant="outline" size="sm" onClick={addRoomOption} data-testid="add-room-option-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Option
              </Button>
            </div>
            <div className="space-y-3">
              {formData.room_options.map((room, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg space-y-2" data-testid={`room-option-${idx}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={room.category || 'room'}
                      onChange={(e) => updateRoomOption(idx, 'category', e.target.value)}
                      className="h-9 px-2 rounded-md bg-white border border-slate-300 text-sm"
                      data-testid={`room-category-${idx}`}
                    >
                      <option value="room">Hotel Room</option>
                      <option value="camping">Camping Pitch</option>
                      <option value="day_pass">Day Pass</option>
                    </select>
                    <Input
                      value={room.type}
                      onChange={(e) => updateRoomOption(idx, 'type', e.target.value)}
                      placeholder={room.category === 'camping' ? 'e.g. Tent Pitch' : 'e.g. Double Room'}
                      className="w-36 bg-white border-slate-300"
                      data-testid={`room-type-${idx}`}
                    />
                    {room.category === 'camping' || room.pricing_type === 'per_unit' ? (
                      <div className="flex items-center gap-1">
                        <span className="text-slate-500 text-xs">£/pitch:</span>
                        <Input
                          type="number"
                          value={room.price_per_unit || 0}
                          onChange={(e) => updateRoomOption(idx, 'price_per_unit', parseFloat(e.target.value) || 0)}
                          className="w-20 bg-white border-slate-300"
                          data-testid={`room-price-unit-${idx}`}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <span className="text-slate-500 text-xs">£/person:</span>
                        <Input
                          type="number"
                          value={room.price_per_person || 0}
                          onChange={(e) => updateRoomOption(idx, 'price_per_person', parseFloat(e.target.value) || 0)}
                          className="w-20 bg-white border-slate-300"
                          data-testid={`room-price-person-${idx}`}
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <span className="text-slate-500 text-xs">Avail:</span>
                      <Input
                        type="number"
                        value={room.available_count || ''}
                        onChange={(e) => updateRoomOption(idx, 'available_count', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="∞"
                        className="w-14 bg-white border-slate-300"
                        min="0"
                        data-testid={`room-available-${idx}`}
                      />
                    </div>
                    {room.category === 'camping' && (
                      <div className="flex items-center gap-1">
                        <span className="text-slate-500 text-xs">Max people:</span>
                        <Input
                          type="number"
                          value={room.max_occupancy || ''}
                          onChange={(e) => updateRoomOption(idx, 'max_occupancy', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="6"
                          className="w-14 bg-white border-slate-300"
                          min="1"
                          data-testid={`room-max-occupancy-${idx}`}
                        />
                      </div>
                    )}
                    {room.booked_count > 0 && (
                      <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                        {room.booked_count} booked
                      </span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRoomOption(idx)}
                      className="text-red-500 ml-auto"
                      data-testid={`remove-room-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Input
                      value={room.description || ''}
                      onChange={(e) => updateRoomOption(idx, 'description', e.target.value)}
                      placeholder="Description (e.g. Electric hookup included)"
                      className="flex-1 min-w-[200px] bg-white border-slate-300 text-sm"
                      data-testid={`room-description-${idx}`}
                    />
                    {formData.event_type === 'festival' && (
                      <label className="flex items-center gap-1.5 text-sm">
                        <input
                          type="checkbox"
                          checked={room.includes_ticket || false}
                          onChange={(e) => updateRoomOption(idx, 'includes_ticket', e.target.checked)}
                          className="rounded"
                          data-testid={`room-includes-ticket-${idx}`}
                        />
                        <span className="text-slate-600">Includes festival ticket</span>
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Ticket Options - Only for Festivals */}
          {formData.event_type === 'festival' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-slate-700">Festival Tickets (Standalone)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addTicketOption} data-testid="add-ticket-option-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Ticket
                </Button>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                Add ticket options for guests who don't need accommodation
              </p>
              <div className="space-y-2">
                {(formData.ticket_options || []).map((ticket, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg flex-wrap" data-testid={`ticket-option-${idx}`}>
                    <Input
                      value={ticket.type}
                      onChange={(e) => updateTicketOption(idx, 'type', e.target.value)}
                      placeholder="e.g. Weekend Pass"
                      className="w-36 bg-white border-slate-300"
                      data-testid={`ticket-type-${idx}`}
                    />
                    <div className="flex items-center gap-1">
                      <span className="text-slate-500 text-xs">Price:</span>
                      <span className="text-slate-500">£</span>
                      <Input
                        type="number"
                        value={ticket.price || 0}
                        onChange={(e) => updateTicketOption(idx, 'price', parseFloat(e.target.value) || 0)}
                        className="w-20 bg-white border-slate-300"
                        data-testid={`ticket-price-${idx}`}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-slate-500 text-xs">Available:</span>
                      <Input
                        type="number"
                        value={ticket.available_count || ''}
                        onChange={(e) => updateTicketOption(idx, 'available_count', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="∞"
                        className="w-16 bg-white border-slate-300"
                        data-testid={`ticket-available-${idx}`}
                      />
                    </div>
                    {ticket.booked_count > 0 && (
                      <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                        {ticket.booked_count} sold
                      </span>
                    )}
                    <Input
                      value={ticket.description || ''}
                      onChange={(e) => updateTicketOption(idx, 'description', e.target.value)}
                      placeholder="Description"
                      className="flex-1 min-w-[100px] bg-white border-slate-300"
                      data-testid={`ticket-description-${idx}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTicketOption(idx)}
                      className="text-red-500"
                      data-testid={`remove-ticket-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add-ons */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-slate-700">Add-ons (Optional)</Label>
              <Button type="button" variant="outline" size="sm" onClick={addAddOn} data-testid="add-addon-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Option
              </Button>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Set a deposit amount per person for add-ons (e.g., coach travel) to secure the booking
            </p>
            <div className="space-y-2">
              {formData.add_ons.map((addOn, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg flex-wrap" data-testid={`addon-${idx}`}>
                  <Input
                    value={addOn.name}
                    onChange={(e) => updateAddOn(idx, 'name', e.target.value)}
                    placeholder="Name (e.g. Coach Travel)"
                    className="flex-1 min-w-[150px] bg-white border-slate-300"
                    data-testid={`addon-name-${idx}`}
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-slate-500 text-xs">Price:</span>
                    <span className="text-slate-500">£</span>
                    <Input
                      type="number"
                      value={addOn.price}
                      onChange={(e) => updateAddOn(idx, 'price', parseFloat(e.target.value) || 0)}
                      placeholder="Price pp"
                      className="w-20 bg-white border-slate-300"
                      data-testid={`addon-price-${idx}`}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-500 text-xs">Deposit:</span>
                    <span className="text-slate-500">£</span>
                    <Input
                      type="number"
                      value={addOn.deposit_amount || 0}
                      onChange={(e) => updateAddOn(idx, 'deposit_amount', parseFloat(e.target.value) || 0)}
                      placeholder="Deposit pp"
                      className="w-20 bg-white border-slate-300"
                      data-testid={`addon-deposit-${idx}`}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeAddOn(idx)}
                    className="text-red-500"
                    data-testid={`remove-addon-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Info */}
          <div>
            <Label className="text-slate-700">Additional Information</Label>
            <Textarea
              value={formData.additional_info}
              onChange={(e) => setFormData(prev => ({ ...prev, additional_info: e.target.value }))}
              placeholder="Any extra details, cancellation policy, etc."
              rows={3}
              className="bg-white border-slate-300"
              data-testid="event-additional-info-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} data-testid="event-dialog-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="event-dialog-submit-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {event ? 'Save Changes' : 'Create Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
